/**
 * Chats list — the target chats for mailing.
 *
 * Features:
 *   - Drag-to-reorder (Framer Motion Reorder.Group, optimistic via TanStack)
 *   - Filter chips (all / channels / groups / users) + "только активные" toggle
 *   - Multi-select (long-press OR explicit "select" button), with a floating
 *     bulk-action bar (enable / disable / delete / assign template).
 *   - Tap → ChatEditSheet
 *   - Empty state with CTA → ChatImportSheet
 *   - Drag-drop bypasses tap (we suppress tap when a drag has happened)
 *
 * Route: /app/chats (?import=1 auto-opens the import sheet)
 */

import { motion, AnimatePresence, Reorder, useDragControls } from "framer-motion";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Clock,
  Eye,
  EyeOff,
  Filter,
  FolderDown,
  GripVertical,
  Hash,
  Lock,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { DragScroll } from "@/components/DragScroll";
import { Button } from "@/design";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useBulkAction,
  useChats,
  useReorderChats,
  type BulkActionType,
  type Chat,
  type PeerType,
} from "@/api/chats";
import { useTemplates } from "@/api/templates";
import { ChatImportSheet } from "@/components/chats/ChatImportSheet";
import { ChatAccountImportSheet } from "@/components/chats/ChatAccountImportSheet";
import { ChatEditSheet } from "@/components/chats/ChatEditSheet";

type KindFilter = "all" | "channel" | "chat" | "user";

export function ChatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: chats, isLoading } = useChats();
  const reorderMut = useReorderChats();
  const bulkMut = useBulkAction();

  const [filter, setFilter] = useState<KindFilter>("all");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [accountImportOpen, setAccountImportOpen] = useState(false);
  const [editing, setEditing] = useState<Chat | null>(null);

  // Auto-open import sheet when arriving from "Добавить чаты"
  useEffect(() => {
    if (searchParams.get("import") === "1") {
      setImportOpen(true);
      const sp = new URLSearchParams(searchParams);
      sp.delete("import");
      setSearchParams(sp, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Local order (so reorder feels instant; we still call mutate for the server)
  const [order, setOrder] = useState<number[]>([]);
  useEffect(() => {
    if (chats) setOrder(chats.map((c) => c.id));
  }, [chats]);

  const byId = useMemo(
    () => new Map((chats ?? []).map((c) => [c.id, c])),
    [chats],
  );

  const filteredOrder = useMemo(() => {
    return order
      .map((id) => byId.get(id))
      .filter((c): c is Chat => !!c)
      .filter((c) => (filter === "all" ? true : c.peer_type === filter))
      .filter((c) => (enabledOnly ? c.is_enabled : true));
  }, [order, byId, filter, enabledOnly]);

  const total = (chats ?? []).length;
  const enabledCount = (chats ?? []).filter((c) => c.is_enabled).length;

  // ────────────────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────────────────

  const handleReorderEnd = (next: Chat[]) => {
    // We only persist on drop, not on every frame.
    const nextIds = next.map((c) => c.id);
    // Merge filtered slice back into the full order (preserving items not visible)
    const visibleSet = new Set(filteredOrder.map((c) => c.id));
    let i = 0;
    const merged = order.map((id) => (visibleSet.has(id) ? nextIds[i++] : id));
    setOrder(merged);
    reorderMut.mutate(
      { chat_ids: merged },
      {
        onError: () => toast.error("Не удалось сохранить порядок"),
      },
    );
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    haptic.select();
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const runBulk = async (action: BulkActionType, templateId?: number) => {
    if (selected.size === 0) return;
    haptic.impact("medium");
    try {
      await bulkMut.mutateAsync({
        chat_ids: [...selected],
        action,
        template_id: templateId,
      });
      haptic.notify("success");
      toast.success(bulkLabel(action, selected.size));
      exitSelect();
    } catch (err) {
      haptic.notify("error");
      toast.error((err as Error)?.message || "Не получилось");
    }
  };

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  return (
    <div className="px-5 pt-4 pb-safe-nav">
      {/* Header */}
      <div className="flex items-center justify-between pt-2 pb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              navigate(-1);
              haptic.select();
            }}
            className="size-9 grid place-items-center rounded-full bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
            aria-label="Назад"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div>
            <div className="text-2xl font-semibold tracking-tight">Чаты</div>
            <div className="text-sm text-text-muted mt-0.5">
              {total === 0
                ? "Импортируйте первый список"
                : `${total} ${pluralRu(total, "чат", "чата", "чатов")} · ${enabledCount} активных`}
            </div>
          </div>
        </div>

        {total > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                haptic.impact("medium");
                setAccountImportOpen(true);
              }}
              className="size-11 rounded-full grid place-items-center bg-white/[0.04] border border-white/[0.06] text-text-muted hover:text-white hover:bg-white/[0.06] transition-colors active:scale-95"
              aria-label="Импортировать из аккаунта"
            >
              <FolderDown className="size-5" />
            </button>
            <button
              onClick={() => {
                haptic.impact("medium");
                setImportOpen(true);
              }}
              className={cn(
                "size-11 rounded-full grid place-items-center",
                "bg-gradient-accent text-white",
                "shadow-[0_8px_24px_-8px_rgba(255,69,0,0.6)]",
                "active:scale-95 transition-transform",
              )}
              aria-label="Импортировать чаты"
            >
              <Plus className="size-5" />
            </button>
          </div>
        )}
      </div>

      {/* Filter row */}
      {total > 0 && (
        <DragScroll className="mb-3 flex items-center gap-2 -mx-1 px-1">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="Все"
            count={total}
          />
          <FilterChip
            active={filter === "channel"}
            onClick={() => setFilter("channel")}
            label="Каналы"
            count={(chats ?? []).filter((c) => c.peer_type === "channel").length}
          />
          <FilterChip
            active={filter === "chat"}
            onClick={() => setFilter("chat")}
            label="Группы"
            count={(chats ?? []).filter((c) => c.peer_type === "chat").length}
          />
          <FilterChip
            active={filter === "user"}
            onClick={() => setFilter("user")}
            label="ЛС"
            count={(chats ?? []).filter((c) => c.peer_type === "user").length}
          />
          <div className="w-px h-5 bg-white/[0.08] shrink-0 mx-1" />
          <button
            onClick={() => {
              setEnabledOnly((v) => !v);
              haptic.select();
            }}
            className={cn(
              "shrink-0 h-8 px-3 rounded-full text-[12px] font-medium",
              "flex items-center gap-1.5 transition-colors",
              enabledOnly
                ? "bg-accent-soft text-accent border border-accent/25"
                : "bg-white/[0.04] text-text-muted border border-white/[0.06] hover:bg-white/[0.06]",
            )}
          >
            <Filter className="size-3" />
            активные
          </button>

          {!selectMode && total > 0 && (
            <button
              onClick={() => {
                setSelectMode(true);
                haptic.select();
              }}
              className="ml-auto shrink-0 h-8 px-3 rounded-full text-[12px] font-medium text-text-muted hover:text-white transition-colors"
            >
              Выбрать
            </button>
          )}
        </DragScroll>
      )}

      {/* Content */}
      <AnimatePresence mode="popLayout">
        {isLoading ? (
          <SkeletonList key="loading" />
        ) : total === 0 ? (
          <EmptyState
            key="empty"
            onImport={() => setImportOpen(true)}
            onImportFromAccount={() => setAccountImportOpen(true)}
          />
        ) : filteredOrder.length === 0 ? (
          <motion.div
            key="filter-empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pt-8 text-center text-sm text-text-muted"
          >
            Нет чатов под этот фильтр
          </motion.div>
        ) : (
          <Reorder.Group
            axis="y"
            values={filteredOrder}
            onReorder={(next) => {
              // setOrder happens on drop (handleReorderEnd) for stability,
              // but Reorder.Group needs the local list to update during drag
              const visibleSet = new Set(filteredOrder.map((c) => c.id));
              let i = 0;
              const merged = order.map((id) =>
                visibleSet.has(id) ? next[i++].id : id,
              );
              setOrder(merged);
            }}
            className="space-y-2"
          >
            {filteredOrder.map((c) => (
              <ChatRow
                key={c.id}
                chat={c}
                selectMode={selectMode}
                selected={selected.has(c.id)}
                onTap={() => {
                  if (selectMode) toggleSelect(c.id);
                  else setEditing(c);
                }}
                onLongPress={() => {
                  if (!selectMode) {
                    setSelectMode(true);
                    toggleSelect(c.id);
                  }
                }}
                onDragEnd={() =>
                  handleReorderEnd(filteredOrder)
                }
              />
            ))}
          </Reorder.Group>
        )}
      </AnimatePresence>

      {/* Bulk action bar */}
      <BulkBar
        visible={selectMode}
        count={selected.size}
        onCancel={exitSelect}
        onEnable={() => runBulk("enable")}
        onDisable={() => runBulk("disable")}
        onDelete={() => {
          if (!confirm(`Удалить ${selected.size} ${pluralRu(selected.size, "чат", "чата", "чатов")}?`))
            return;
          runBulk("delete");
        }}
        onClearTemplate={() => runBulk("clear_template")}
        onAssignTemplate={(tid) => runBulk("assign_template", tid)}
      />

      {/* Sheets */}
      <ChatImportSheet
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
      <ChatAccountImportSheet
        open={accountImportOpen}
        onClose={() => setAccountImportOpen(false)}
      />
      <ChatEditSheet
        open={!!editing}
        onClose={() => setEditing(null)}
        chat={editing}
      />
    </div>
  );
}

// ============================================================
// Row
// ============================================================

function ChatRow({
  chat,
  selectMode,
  selected,
  onTap,
  onLongPress,
  onDragEnd,
}: {
  chat: Chat;
  selectMode: boolean;
  selected: boolean;
  onTap: () => void;
  onLongPress: () => void;
  onDragEnd: () => void;
}) {
  const controls = useDragControls();
  const draggedRef = useRef(false);
  const longPressTimer = useRef<number | null>(null);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      haptic.impact("medium");
      onLongPress();
    }, 500);
    // Suppress for control button — handled by stopPropagation in handle
    void e;
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  return (
    <Reorder.Item
      value={chat}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => {
        draggedRef.current = true;
        clearLongPress();
      }}
      onDragEnd={() => {
        onDragEnd();
        setTimeout(() => (draggedRef.current = false), 100);
      }}
      whileDrag={{ scale: 1.02, zIndex: 10 }}
      className="touch-none"
    >
      <div
        onPointerDown={handlePointerDown}
        onPointerUp={clearLongPress}
        onPointerCancel={clearLongPress}
        onClick={() => {
          if (!draggedRef.current) onTap();
        }}
        className={cn(
          "relative rounded-2xl p-3 flex items-center gap-3 cursor-pointer",
          "bg-white/[0.02] border border-white/[0.06]",
          "hover:bg-white/[0.04] hover:border-white/[0.1] transition-colors",
          "active:scale-[0.995]",
          selected && "bg-accent/[0.06] border-accent/30",
          !chat.is_enabled && "opacity-60",
        )}
      >
        {/* Select checkbox (only in select mode) */}
        <AnimatePresence initial={false}>
          {selectMode && (
            <motion.div
              key="check"
              initial={{ width: 0, opacity: 0, marginRight: 0 }}
              animate={{ width: 24, opacity: 1, marginRight: 0 }}
              exit={{ width: 0, opacity: 0, marginRight: 0 }}
              className="overflow-hidden shrink-0"
            >
              <div
                className={cn(
                  "size-6 rounded-md border grid place-items-center transition-colors",
                  selected
                    ? "bg-accent border-accent"
                    : "border-white/[0.15] bg-transparent",
                )}
              >
                {selected && <Check className="size-3.5 text-white" />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Type icon */}
        <div
          className={cn(
            "size-10 rounded-xl grid place-items-center shrink-0",
            peerStyle(chat.peer_type),
          )}
        >
          <PeerGlyph type={chat.peer_type} />
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <div className="text-[14px] font-medium truncate">{chat.title}</div>
            {chat.topic_message_id != null && (
              <Hash className="size-3 text-text-dim shrink-0" />
            )}
          </div>
          <div className="text-[11px] text-text-muted truncate">{chat.chat_ref}</div>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {chat.guarantor && (
              <Pill icon={<ShieldCheck className="size-2.5" />} color="accent">
                @{chat.guarantor}
              </Pill>
            )}
            {chat.custom_template_id !== null && (
              <Pill icon={<Megaphone className="size-2.5" />} color="info">
                свой шаблон
              </Pill>
            )}
            {chat.custom_schedule_enabled && (
              <Pill icon={<Clock className="size-2.5" />} color="info">
                {chat.custom_posts_per_day}/сут · {chat.custom_interval_minutes} мин
              </Pill>
            )}
            {chat.paused_until && new Date(chat.paused_until) > new Date() && (
              <Pill icon={<Lock className="size-2.5" />} color="warning">
                на паузе
              </Pill>
            )}
            {chat.consecutive_failures >= 3 && (
              <Pill color="danger">
                {chat.consecutive_failures} ошибок подряд
              </Pill>
            )}
          </div>
        </div>

        {/* Drag handle */}
        {!selectMode && (
          <button
            onPointerDown={(e) => {
              e.stopPropagation();
              clearLongPress();
              controls.start(e);
              haptic.impact("light");
            }}
            className="size-9 grid place-items-center rounded-lg text-text-dim hover:text-white hover:bg-white/[0.05] transition-colors shrink-0 cursor-grab active:cursor-grabbing"
            aria-label="Перетащить"
          >
            <GripVertical className="size-4" />
          </button>
        )}

        {!selectMode && (
          <ChevronRight className="size-4 text-text-faint shrink-0 -ml-1" />
        )}
      </div>
    </Reorder.Item>
  );
}

// ============================================================
// Sub-components
// ============================================================

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={() => {
        onClick();
        haptic.select();
      }}
      className={cn(
        "shrink-0 h-8 px-3 rounded-full text-[12px] font-medium",
        "flex items-center gap-1.5 transition-colors",
        active
          ? "bg-accent text-white shadow-[0_4px_12px_-4px_rgba(255,69,0,0.6)]"
          : "bg-white/[0.04] text-text-muted border border-white/[0.06] hover:bg-white/[0.06]",
      )}
    >
      {label}
      <span
        className={cn(
          "text-[10px] tabular-nums px-1 rounded",
          active ? "bg-white/20" : "bg-white/[0.08]",
        )}
      >
        {count}
      </span>
    </button>
  );
}

function Pill({
  children,
  icon,
  color,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  color: "accent" | "info" | "warning" | "danger";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-md",
        color === "accent" && "bg-accent/12 text-accent",
        color === "info" && "bg-info/15 text-info",
        color === "warning" && "bg-warning/15 text-warning",
        color === "danger" && "bg-danger/15 text-danger",
      )}
    >
      {icon}
      {children}
    </span>
  );
}

function PeerGlyph({ type }: { type: PeerType }) {
  if (type === "channel") return <Megaphone className="size-4" />;
  if (type === "user") return <UserIcon className="size-4" />;
  return <MessageSquare className="size-4" />;
}

function peerStyle(type: PeerType): string {
  if (type === "channel") return "bg-accent-soft text-accent";
  if (type === "user") return "bg-info-soft text-info";
  return "bg-white/[0.06] text-text-muted";
}

// ============================================================
// Bulk action bar
// ============================================================

function BulkBar({
  visible,
  count,
  onCancel,
  onEnable,
  onDisable,
  onDelete,
  onClearTemplate,
  onAssignTemplate,
}: {
  visible: boolean;
  count: number;
  onCancel: () => void;
  onEnable: () => void;
  onDisable: () => void;
  onDelete: () => void;
  onClearTemplate: () => void;
  onAssignTemplate: (templateId: number) => void;
}) {
  const [tplPickerOpen, setTplPickerOpen] = useState(false);
  const { data: templates } = useTemplates();
  const mailTemplates = (templates ?? []).filter((t) => t.kind === "mail");

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed inset-x-0 bottom-[88px] z-30 px-4 pointer-events-none"
        >
          <div className="mx-auto max-w-md pointer-events-auto">
            <div
              className={cn(
                "glass-strong rounded-2xl border border-white/[0.08]",
                "shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)]",
                "overflow-hidden",
              )}
            >
              <div className="flex items-center px-3 py-2 border-b border-white/[0.04]">
                <button
                  onClick={onCancel}
                  className="size-8 grid place-items-center rounded-full hover:bg-white/[0.06] transition-colors"
                  aria-label="Отменить выбор"
                >
                  <X className="size-4" />
                </button>
                <div className="ml-2 text-sm font-medium">
                  Выбрано: {count}
                </div>
              </div>
              <div className="flex items-center justify-around px-2 py-2.5">
                <BulkBtn
                  icon={<Eye className="size-4" />}
                  label="Вкл"
                  onClick={onEnable}
                  disabled={count === 0}
                />
                <BulkBtn
                  icon={<EyeOff className="size-4" />}
                  label="Пауза"
                  onClick={onDisable}
                  disabled={count === 0}
                />
                <BulkBtn
                  icon={<Megaphone className="size-4" />}
                  label="Шаблон"
                  onClick={() => setTplPickerOpen((v) => !v)}
                  disabled={count === 0}
                />
                <BulkBtn
                  icon={<Trash2 className="size-4" />}
                  label="Удалить"
                  onClick={onDelete}
                  disabled={count === 0}
                  tone="danger"
                />
              </div>

              <AnimatePresence>
                {tplPickerOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-white/[0.04] overflow-hidden"
                  >
                    <div className="max-h-[200px] overflow-y-auto">
                      <button
                        onClick={() => {
                          setTplPickerOpen(false);
                          onClearTemplate();
                        }}
                        className="w-full text-left px-4 py-2.5 text-[13px] text-text-muted hover:bg-white/[0.03] flex items-center gap-2"
                      >
                        <X className="size-3.5" />
                        Сбросить шаблоны
                      </button>
                      {mailTemplates.length === 0 ? (
                        <div className="px-4 py-3 text-[12px] text-text-muted text-center">
                          Нет шаблонов рассылки
                        </div>
                      ) : (
                        mailTemplates.map((t) => (
                          <button
                            key={t.id}
                            onClick={() => {
                              setTplPickerOpen(false);
                              onAssignTemplate(t.id);
                            }}
                            className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-white/[0.03] flex items-center gap-2"
                          >
                            <Sparkles className="size-3.5 text-accent" />
                            {t.name}
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BulkBtn({
  icon,
  label,
  onClick,
  disabled,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "danger";
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl transition-colors",
        "hover:bg-white/[0.05] disabled:opacity-40 disabled:pointer-events-none",
        tone === "danger" ? "text-danger" : "text-text",
      )}
    >
      {icon}
      <span className="text-[10px] uppercase tracking-wider font-medium">
        {label}
      </span>
    </button>
  );
}

// ============================================================
// Empty + skeleton
// ============================================================

function EmptyState({
  onImport,
  onImportFromAccount,
}: {
  onImport: () => void;
  onImportFromAccount: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative pt-6 pb-8"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-10 w-[80%] aspect-square rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(255,107,53,0.35) 0%, rgba(255,69,0,0.12) 38%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />

      <div className="relative flex flex-col items-center text-center px-6 space-y-5">
        <div className="relative size-32">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border border-dashed border-accent/30"
          />
          <motion.div
            animate={{ scale: [0.95, 1.04, 0.95] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-4 rounded-full border border-accent/15"
          />
          <div className="absolute inset-8 rounded-[24px] bg-gradient-accent grid place-items-center shadow-[0_20px_60px_-12px_rgba(255,69,0,0.6)]">
            <MessageCircle className="size-9 text-white" strokeWidth={2.2} />
          </div>
          <motion.div
            animate={{ rotate: [0, 15, -15, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-1 -right-1 size-7 rounded-xl bg-surface border border-white/[0.08] grid place-items-center"
          >
            <Sparkles className="size-3.5 text-accent" />
          </motion.div>
        </div>

        <div className="space-y-2 max-w-[300px]">
          <div className="text-[22px] font-semibold tracking-tight leading-tight">
            Добавьте первые
            <br />
            <span className="text-gradient-accent">целевые чаты</span>
          </div>
          <div className="text-sm text-text-muted leading-relaxed">
            Вставьте ссылки на каналы, группы и&nbsp;форумы — мы&nbsp;разрешим их
            через ваш аккаунт и&nbsp;найдём гарантов автоматически.
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full max-w-[300px] mt-2">
          <Button size="lg" leftIcon={<FolderDown />} onClick={onImportFromAccount} fullWidth>
            Из аккаунта
          </Button>
          <Button size="lg" variant="secondary" leftIcon={<Plus />} onClick={onImport} fullWidth>
            По ссылкам
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function SkeletonList() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-2"
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-[78px] rounded-2xl bg-white/[0.025] border border-white/[0.06] relative overflow-hidden"
        >
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/[0.04] to-transparent" />
        </div>
      ))}
    </motion.div>
  );
}

// ============================================================
// Utils
// ============================================================

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function bulkLabel(action: BulkActionType, n: number): string {
  switch (action) {
    case "enable":
      return `Включено: ${n}`;
    case "disable":
      return `На паузе: ${n}`;
    case "delete":
      return `Удалено: ${n}`;
    case "assign_template":
      return `Шаблон назначен (${n})`;
    case "clear_template":
      return `Шаблоны сброшены (${n})`;
  }
}
